import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { brl, brlPrecise, partyStyle } from "../lib/api";
import { HOME } from "../constants/testIds";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";

export default function RankingTable({
  items,
  type, // "servidor" | "politico"
  loading,
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="civic-card">
        <div className="animate-pulse space-y-2 p-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-10 rounded-sm bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div
        className="civic-card flag-stripe px-6 py-16 text-center"
        data-testid={HOME.emptyState}
      >
        <div className="overline mb-2 text-emerald-800">
          Nenhum registro encontrado
        </div>
        <h3 className="mb-2 text-2xl font-semibold text-slate-900 font-display">
          A base ainda está sendo populada
        </h3>
        <p className="mx-auto max-w-md text-sm text-slate-500">
          Clique em <b>Atualizar dados</b> no cabeçalho para iniciar a ingestão
          a partir das APIs oficiais. Isso pode levar alguns minutos na primeira
          execução.
        </p>
      </div>
    );
  }

  return (
    <div className="civic-card overflow-hidden">
      <Table data-testid={HOME.rankingTable}>
        <TableHeader>
          <TableRow className="bg-slate-50/60 hover:bg-slate-50/60">
            <TableHead className="w-16 text-xs uppercase tracking-wider text-slate-500">
              #
            </TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-slate-500">
              Nome
            </TableHead>
            <TableHead className="hidden md:table-cell text-xs uppercase tracking-wider text-slate-500">
              {type === "politico" ? "Partido / UF" : "Cargo / Órgão"}
            </TableHead>
            <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">
              Gasto anual
            </TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => {
            const top = it.posicao <= 3 ? it.posicao : undefined;
            const partido = it.tag || "";
            const style = type === "politico" ? partyStyle(partido) : null;
            return (
              <TableRow
                key={it.id}
                className="civic-row cursor-pointer border-b border-slate-100"
                onClick={() => navigate(`/pessoa/${type}/${it.id}`)}
                data-testid={`${HOME.rankingRow}-${it.id}`}
              >
                <TableCell className="align-middle">
                  <span className="rank-chip" data-top={top}>
                    {String(it.posicao).padStart(2, "0")}
                  </span>
                </TableCell>
                <TableCell className="align-middle">
                  <div className="font-semibold text-slate-900">{it.nome}</div>
                  <div className="mt-0.5 text-xs text-slate-500 md:hidden">
                    {it.subtitulo}
                  </div>
                </TableCell>
                <TableCell className="hidden align-middle md:table-cell">
                  {type === "politico" ? (
                    <div className="flex items-center gap-2">
                      <span
                        className="party-badge"
                        style={{
                          backgroundColor: style.bg,
                          color: style.fg,
                          borderColor: style.border,
                        }}
                      >
                        {partido || "—"}
                      </span>
                      <span className="text-sm text-slate-600">
                        {it.subtitulo}
                      </span>
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <div className="truncate text-sm text-slate-800">
                        {it.subtitulo || "—"}
                      </div>
                      {it.tag && (
                        <span className="gov-badge mt-1">{it.tag}</span>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold text-slate-900 font-mono-plex">
                  <span
                    title={brlPrecise(it.gasto_total_anual)}
                    aria-label={brlPrecise(it.gasto_total_anual)}
                  >
                    {brl(it.gasto_total_anual)}
                  </span>
                </TableCell>
                <TableCell className="text-slate-400">
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
